/**
 * 차트 및 테이블 데이터 유효성 검증 유틸리티
 * @author 젠이츠
 */

/**
 * chartData가 비어있는지 확인
 * @param {Object} chartData - { "난방": [null, null, null], ... } 형태
 * @returns {Boolean} - 데이터가 없으면 true, 있으면 false
 */
export function isEmptyChartData(chartData) {
    if (!chartData || typeof chartData !== "object") return true;

    const keys = Object.keys(chartData);
    if (keys.length === 0) return true;

    // 모든 배열이 null만 포함하거나 모두 null인지 확인
    return keys.every((key) => {
        const arr = chartData[key];
        if (!arr || !Array.isArray(arr)) return true;
        return arr.length === 0 || arr.every((item) => item == null);
    });
}

/**
 * 단일 tableData 객체가 비어있는지 확인
 * @param {Object} tableData - { prevMonth: null, currentMonth: null, ... } 형태
 * @param {String} timeDsvn - 'MONTH' 또는 'YEAR'
 * @returns {Boolean} - 데이터가 없으면 true, 있으면 false
 */
function isEmptySingleTableData(tableData, timeDsvn = "MONTH") {
    if (timeDsvn === "MONTH") {
        return tableData.prevMonth == null && tableData.currentMonth == null && tableData.prevYearMonth == null;
    } else {
        return tableData.prevYear == null && tableData.currentYear == null && tableData.prevPrevYear == null;
    }
}

/**
 * tableData가 비어있는지 확인 (단일 객체 또는 객체의 객체 모두 지원)
 * @param {Object} tableData -
 *   단일 형태: { prevMonth: null, currentMonth: null, ... }
 *   다중 형태: { "난방": { prevMonth: null, ... }, "냉방": { ... }, ... }
 * @param {String} timeDsvn - 'MONTH' 또는 'YEAR'
 * @returns {Boolean} - 데이터가 없으면 true, 있으면 false
 */
export function isEmptyTableData(tableData, timeDsvn = "MONTH") {
    if (!tableData || typeof tableData !== "object") return true;

    // 객체의 첫 번째 값이 객체인지 확인 (객체의 객체 형태인지 판단)
    const firstValue = Object.values(tableData)[0];
    const isNestedObject =
        firstValue &&
        typeof firstValue === "object" &&
        !Array.isArray(firstValue) &&
        (firstValue.hasOwnProperty("prevMonth") || firstValue.hasOwnProperty("prevYear"));

    if (isNestedObject) {
        // 객체의 객체 형태: 모든 키의 tableData가 비어있는지 확인
        return Object.values(tableData).every((item) => {
            if (!item || typeof item !== "object") return true;
            return isEmptySingleTableData(item, timeDsvn);
        });
    } else {
        // 단일 객체 형태: 기존 로직 사용
        return isEmptySingleTableData(tableData, timeDsvn);
    }
}

/**
 * chartData와 tableData가 모두 비어있는지 확인하는 범용 함수
 * @param {Object} data - { chartData: {...}, tableData: {...} } 형태
 * @param {String} timeDsvn - 'MONTH' 또는 'YEAR' (tableData 검증 시 사용)
 * @returns {Boolean} - 데이터가 없으면 true, 있으면 false
 */
export function isEmptyChartAndTableData(data, timeDsvn = "MONTH") {
    if (!data) return true;

    const { chartData, tableData } = data;

    // chartData 검증
    const isEmptyChart = isEmptyChartData(chartData);

    // tableData 검증
    const isEmptyTable = isEmptyTableData(tableData, timeDsvn);

    // 둘 다 비어있어야 true
    return isEmptyChart && isEmptyTable;
}
